import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import toast from "react-hot-toast";

import { AlertModal, Button } from "@/components/ui";
import { sanitizeUserFacingErrorText } from "@/lib/errorMessages";
import { useAuthStore } from "@/store/authStore";
import { activityService } from "@/services/activityService";
import { bookingService } from "@/services/bookingService";
import { formatMoney } from "@/lib/money";
import type {
  Activity,
  BookingListFilters,
  ReceiptAnalyzeResponse,
} from "@/types";
import { Permission } from "@/types";

import {
  BookingsNeedsAttentionSection,
  type AttentionActionFilter,
} from "./BookingsNeedsAttentionSection";
import type { BookingListRow } from "./bookingViewModel";
import { ReceiptConfirmModal } from "./ReceiptConfirmModal";
import { ReceiptManualAmountModal } from "./ReceiptManualAmountModal";
import { ReceiptOcrFailureModal } from "./ReceiptOcrFailureModal";

interface ReceiptConfirmState {
  bookingId: string;
  bookingType: "service" | "receipt_based";
  storagePath: string;
  analyzeResult: ReceiptAnalyzeResponse;
}

interface ManualAmountState {
  bookingId: string;
  storagePath: string;
}

interface ReceiptOcrFailureState {
  booking: BookingListRow;
  storagePath: string;
  message: string;
  manualAmountRequired: boolean;
  remainingValidationAttempts?: number;
  maxValidationAttempts?: number;
}

/** Activity owners are scoped to their own activities server-side. */
const OWN_BOOKINGS_SCOPE: BookingListFilters = {};

/**
 * Same "needs attention" section as the Bookings tab, for partner surfaces
 * (e.g. dashboard) that should surface actionable bookings immediately.
 */
export function PartnerNeedsAttentionPanel() {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("bookings");
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();

  const [attentionFilter, setAttentionFilter] =
    useState<AttentionActionFilter>("all");
  const [processingByBookingId, setProcessingByBookingId] = useState<
    Record<string, boolean>
  >({});
  const [receiptConfirm, setReceiptConfirm] =
    useState<ReceiptConfirmState | null>(null);
  const [manualAmountEntry, setManualAmountEntry] =
    useState<ManualAmountState | null>(null);
  const [receiptOcrFailure, setReceiptOcrFailure] =
    useState<ReceiptOcrFailureState | null>(null);
  const [confirmingReceipt, setConfirmingReceipt] = useState(false);
  const [submittingManualAmount, setSubmittingManualAmount] = useState(false);
  const [receiptAlert, setReceiptAlert] = useState<{
    title: string;
    message: string;
    bookingId?: string;
  } | null>(null);
  const [fileInputResetKey, setFileInputResetKey] = useState<
    Record<string, number>
  >({});

  const { data: activities = [], isLoading: isLoadingActivities } = useQuery<
    Activity[]
  >({
    queryKey: ["activities"],
    queryFn: () => activityService.getAll(),
  });

  const hasActivities = activities.length > 0;

  const activityMap = useMemo(() => {
    const map = new Map<string, Activity>();
    activities.forEach((a) => map.set(a.id, a));
    return map;
  }, [activities]);

  const canInteractHandshake =
    hasPermission(Permission.HANDSHAKES_SESSION_CONFIRM) ||
    hasPermission(Permission.HANDSHAKES_MESSAGE_SEND);

  const handleReceiptUpload = async (
    booking: BookingListRow,
    file?: File | null,
  ) => {
    if (!file) return;
    const bookingId = booking.id;
    const bookingType = booking.type;

    setProcessingByBookingId((prev) => ({ ...prev, [bookingId]: true }));
    setReceiptOcrFailure(null);
    let uploadedPath: string | undefined;

    try {
      const uploadResult = await bookingService.uploadSellerReceipt(
        bookingId,
        file,
      );
      uploadedPath = uploadResult.path;

      const analyzeResult = await bookingService.analyzeSellerReceipt(
        bookingId,
        uploadResult.path,
      );

      if (!analyzeResult.ocr_success) {
        setReceiptOcrFailure({
          booking,
          storagePath: uploadResult.path,
          message:
            analyzeResult.error_message || t("receiptOcrUnreadableFallback"),
          manualAmountRequired: analyzeResult.manual_amount_required === true,
          remainingValidationAttempts:
            analyzeResult.remaining_validation_attempts,
          maxValidationAttempts: analyzeResult.max_validation_attempts,
        });
        return;
      }

      setReceiptConfirm({
        bookingId,
        bookingType,
        storagePath: uploadResult.path,
        analyzeResult,
      });
    } catch (e) {
      setReceiptOcrFailure({
        booking,
        storagePath: uploadedPath ?? booking.latestUploadPath ?? "",
        message: t("receiptUploadErrorMessage"),
        manualAmountRequired: booking.manualAmountRequired === true,
      });
      console.error(e);
    } finally {
      setProcessingByBookingId((prev) => ({ ...prev, [bookingId]: false }));
    }
  };

  const handleConfirmReceipt = async () => {
    if (!receiptConfirm) return;

    const { bookingId, storagePath, analyzeResult } = receiptConfirm;
    const totalCents = analyzeResult.extracted_total_cents ?? 0;

    setConfirmingReceipt(true);
    try {
      await bookingService.registerSellerReceipt(
        bookingId,
        storagePath,
        totalCents,
        "OCR",
      );
      toast.success(
        t("toastPartnerUploaded", {
          total: analyzeResult.extracted_total_display,
        }),
      );
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
    } catch (e) {
      setReceiptAlert({
        title: t("receiptConfirmErrorTitle"),
        message: t("receiptConfirmErrorMessage"),
      });
      console.error(e);
    } finally {
      setConfirmingReceipt(false);
    }
    setReceiptConfirm(null);
  };

  const handleRejectReceipt = async () => {
    if (!receiptConfirm) return;

    const { bookingId, storagePath } = receiptConfirm;
    setConfirmingReceipt(true);
    try {
      const result = await bookingService.rejectSellerReceipt(
        bookingId,
        storagePath,
      );
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
      void queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      if (result.manual_amount_required) {
        setManualAmountEntry({ bookingId, storagePath });
      } else {
        setFileInputResetKey((prev) => ({
          ...prev,
          [bookingId]: (prev[bookingId] ?? 0) + 1,
        }));
      }
    } catch (e) {
      setReceiptAlert({
        title: t("receiptConfirmErrorTitle"),
        message: t("receiptConfirmErrorMessage"),
      });
      console.error(e);
    } finally {
      setConfirmingReceipt(false);
      setReceiptConfirm(null);
    }
  };

  const handleManualAmountSubmit = async (totalCents: number) => {
    if (!manualAmountEntry) return;

    const { bookingId, storagePath } = manualAmountEntry;
    setSubmittingManualAmount(true);
    try {
      await bookingService.registerSellerReceipt(
        bookingId,
        storagePath,
        totalCents,
        "MANUAL",
      );
      toast.success(
        t("toastPartnerManualAmount", { total: formatMoney(totalCents) }),
      );
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
      void queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
      setManualAmountEntry(null);
    } catch (e) {
      setReceiptAlert({
        title: t("receiptConfirmErrorTitle"),
        message: t("receiptConfirmErrorMessage"),
      });
      console.error(e);
    } finally {
      setSubmittingManualAmount(false);
    }
  };

  // Hide only after activities resolve empty. While either query is in flight,
  // keep the section mounted so the loading card is visible (activities and
  // bookings fetch in parallel after the dashboard shell finishes).
  if (!isLoadingActivities && !hasActivities) {
    return null;
  }

  return (
    <>
      <BookingsNeedsAttentionSection
        scope={OWN_BOOKINGS_SCOPE}
        activityMap={activityMap}
        filter={attentionFilter}
        onFilterChange={setAttentionFilter}
        bookingsListPath="/bookings"
        adminMode={false}
        canInteractHandshake={canInteractHandshake}
        processingByBookingId={processingByBookingId}
        fileInputResetKey={fileInputResetKey}
        isLoading={isLoadingActivities}
        onReceiptUpload={(booking, file) =>
          void handleReceiptUpload(booking, file)
        }
        onManualAmountClick={(bookingId, storagePath) =>
          setManualAmountEntry({ bookingId, storagePath })
        }
        footerAction={
          <Link to="/bookings">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              rightIcon={<ArrowRight className="h-4 w-4" />}
            >
              {t("needsAttention.seeAllBookings")}
            </Button>
          </Link>
        }
      />

      {receiptConfirm ? (
        <ReceiptConfirmModal
          analyzeResult={receiptConfirm.analyzeResult}
          confirmingReceipt={confirmingReceipt}
          onConfirm={() => void handleConfirmReceipt()}
          onRetake={() => void handleRejectReceipt()}
        />
      ) : null}

      {receiptOcrFailure ? (
        <ReceiptOcrFailureModal
          message={receiptOcrFailure.message}
          manualAmountRequired={receiptOcrFailure.manualAmountRequired}
          remainingValidationAttempts={
            receiptOcrFailure.remainingValidationAttempts
          }
          maxValidationAttempts={receiptOcrFailure.maxValidationAttempts}
          isProcessing={
            processingByBookingId[receiptOcrFailure.booking.id] ?? false
          }
          onFileRetry={(file) =>
            void handleReceiptUpload(receiptOcrFailure.booking, file)
          }
          onManualAmount={() => {
            const path =
              receiptOcrFailure.storagePath ||
              receiptOcrFailure.booking.latestUploadPath;
            if (!path) return;
            setManualAmountEntry({
              bookingId: receiptOcrFailure.booking.id,
              storagePath: path,
            });
            setReceiptOcrFailure(null);
          }}
        />
      ) : null}

      {manualAmountEntry ? (
        <ReceiptManualAmountModal
          isSubmitting={submittingManualAmount}
          onSubmit={(totalCents) => void handleManualAmountSubmit(totalCents)}
          onClose={() => setManualAmountEntry(null)}
        />
      ) : null}

      {receiptAlert ? (
        <AlertModal
          closeLabel={tCommon("close")}
          isOpen={!!receiptAlert}
          onClose={() => {
            const bookingId = receiptAlert.bookingId;
            if (bookingId) {
              setFileInputResetKey((prev) => ({
                ...prev,
                [bookingId]: (prev[bookingId] ?? 0) + 1,
              }));
            }
            setReceiptAlert(null);
          }}
          title={receiptAlert.title}
          message={
            sanitizeUserFacingErrorText(
              receiptAlert.message,
              tCommon("somethingWentWrong"),
            ) ?? tCommon("somethingWentWrong")
          }
          variant="error"
          buttonText={t("alertGotIt")}
        />
      ) : null}
    </>
  );
}
